// Bibliothèque de templates du coach — page dédiée accessible depuis l'onglet
// "Templates" de la sidebar. Par défaut on voit les templates du coach.
// Un sélecteur en haut permet de basculer sur un élève pour voir/éditer ses
// templates, gérer ses dossiers et importer un template depuis la bibliothèque
// du coach vers l'élève.
import React, { useEffect, useMemo, useState } from "react";
import TemplateFolderMenu from "./TemplateFolderMenu";
import {
	getStudentTemplates,
	getStudentTemplateFolders,
	saveStudentTemplateFolder,
	deleteStudentTemplateFolder,
	assignStudentTemplateFolder,
	deleteTraining,
	createTraining,
} from "../../services/authService";

// ─── Modale d'import : coach → élève ───
function ImportFromCoachModal({ coachTemplates, coachFolders, coachAssoc, studentTemplates, onClose, onImport }) {
	const [q, setQ] = useState("");
	// null = "Tous les templates", sinon on filtre sur ce dossier coach précis.
	const [activeFolderKey, setActiveFolderKey] = useState(null);
	const [importingId, setImportingId] = useState(null);
	const [error, setError] = useState("");

	const normalize = (s) => (s || "").trim().toLowerCase();
	const studentNames = useMemo(
		() => new Set((studentTemplates || []).map((t) => normalize(t.nom ?? t.Nom))),
		[studentTemplates]
	);

	// Dossiers actifs (non supprimés) uniquement, triés par nom pour un ordre stable.
	const activeFolders = useMemo(() => {
		const arr = (coachFolders || []).filter((f) => !(f.Supprime ?? f.supprime));
		arr.sort((a, b) => (a.NomDossier ?? a.nomDossier ?? "").localeCompare(b.NomDossier ?? b.nomDossier ?? ""));
		return arr;
	}, [coachFolders]);

	// Index : pour chaque folderKey → Set des templateKey qui y appartiennent.
	// Permet un filtre O(1) au lieu de scan sur toutes les associations.
	const folderTemplateIndex = useMemo(() => {
		const m = new Map();
		(coachAssoc || []).forEach((a) => {
			if (a.Supprime ?? a.supprime) return;
			const fk = a.FolderKey ?? a.folderKey;
			const tk = a.TemplateKey ?? a.templateKey;
			if (fk == null || tk == null) return;
			if (!m.has(fk)) m.set(fk, new Set());
			m.get(fk).add(tk);
		});
		return m;
	}, [coachAssoc]);

	// Compte de templates par dossier — utile pour le pill "Presse (12)".
	const folderCount = (folderKey) => (folderTemplateIndex.get(folderKey)?.size ?? 0);

	const list = useMemo(() => {
		const term = q.trim().toLowerCase();
		const folderTplKeys = activeFolderKey != null ? folderTemplateIndex.get(activeFolderKey) : null;
		return (coachTemplates || [])
			.filter((t) => {
				if (!folderTplKeys) return true;
				const key = t.Key ?? t.key;
				return folderTplKeys.has(key);
			})
			.map((t) => ({ ...t, _already: studentNames.has(normalize(t.nom ?? t.Nom)) }))
			.filter((t) => !term || (t.nom ?? t.Nom ?? "").toLowerCase().includes(term));
	}, [coachTemplates, studentNames, q, activeFolderKey, folderTemplateIndex]);

	const handleImport = async (tpl) => {
		if (tpl._already) return;
		const tplId = tpl.Id ?? tpl.id;
		setImportingId(tplId);
		setError("");
		const res = await onImport(tpl);
		setImportingId(null);
		if (!res?.ok) setError("Erreur lors de l'import");
	};

	return (
		<div className="mens-modal-overlay" onClick={onClose}>
			<div
				className="mens-modal"
				onClick={(e) => e.stopPropagation()}
				style={{
					// Modale grande et fixe. Réduite à 85vh pour ne pas passer sous
					// le header du site (qui reste au-dessus dans son propre z-index).
					// Taille constante quel que soit le contenu.
					width: "90vw",
					height: "80vh",
					maxWidth: "none",
					maxHeight: "none",
					display: "flex",
					flexDirection: "column",
					overflow: "hidden",
				}}
			>
				<div className="mens-modal-header">
					<h3>Importer depuis ma bibliothèque</h3>
					<button className="mens-modal-close" onClick={onClose}>×</button>
				</div>

				{/* Filtre par dossier — n'apparaît que si au moins un dossier existe côté coach.
				    "Tous" en premier pour retirer le filtre. Chaque pill affiche le compte de
				    templates dans son dossier pour aider la navigation. */}
				{activeFolders.length > 0 && (
					<div className="import-modal-folders">
						<button
							type="button"
							className={`import-modal-folder-pill ${activeFolderKey === null ? "active" : ""}`}
							onClick={() => setActiveFolderKey(null)}
						>
							Tous
							<span className="import-modal-folder-count">{(coachTemplates || []).length}</span>
						</button>
						{activeFolders.map((f) => {
							const fk = f.Key ?? f.key;
							const nom = f.NomDossier ?? f.nomDossier ?? "Dossier";
							const count = folderCount(fk);
							return (
								<button
									key={fk}
									type="button"
									className={`import-modal-folder-pill ${activeFolderKey === fk ? "active" : ""}`}
									onClick={() => setActiveFolderKey(fk)}
									title={nom}
								>
									<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
										<path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
									</svg>
									<span className="import-modal-folder-name">{nom}</span>
									<span className="import-modal-folder-count">{count}</span>
								</button>
							);
						})}
					</div>
				)}

				<div className="editor-panel-exercises-search" style={{ marginTop: 8 }}>
					<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
						<circle cx="11" cy="11" r="8" />
						<line x1="21" y1="21" x2="16.65" y2="16.65" />
					</svg>
					<input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un modèle" autoFocus />
					{q && <button className="editor-panel-exercises-clear" onClick={() => setQ("")}>×</button>}
				</div>
				{error && <div className="mens-modal-error">{error}</div>}
				<div
					className="import-modal-list"
					style={{
						// La liste prend tout l'espace vertical restant dans la modale flex
						// (au lieu d'un maxHeight fixe). Sur une modale de 90vh, ça donne
						// facilement 500-600px de zone scrollable = 10-12 templates visibles
						// d'un coup au lieu de 5.
						flex: 1,
						minHeight: 200,
						overflowY: "auto",
						marginTop: 12,
					}}
				>
					{list.length === 0 ? (
						<div className="coach-exercises-library-empty">
							{q.trim()
								? "Aucun modèle ne correspond à la recherche"
								: activeFolderKey != null
									? "Ce dossier est vide"
									: "Aucun modèle"}
						</div>
					) : list.map((tpl) => {
						const tplId = tpl.Id ?? tpl.id;
						const nom = tpl.Nom ?? tpl.nom ?? "Sans nom";
						const exos = tpl.ListExercice ?? tpl.listExercice ?? tpl.exercices ?? [];
						return (
							<button
								key={tplId} type="button"
								disabled={tpl._already || importingId === tplId}
								onClick={() => handleImport(tpl)}
								style={{
									display: "flex", alignItems: "center", gap: 10, width: "100%",
									padding: "10px 12px", marginBottom: 6, borderRadius: 8,
									border: "1px solid var(--line-soft)", background: tpl._already ? "rgba(0,0,0,0.03)" : "transparent",
									color: "var(--ink)", cursor: tpl._already ? "default" : "pointer",
									textAlign: "left", opacity: tpl._already ? 0.55 : 1,
								}}
							>
								<div style={{ flex: 1, minWidth: 0 }}>
									<div style={{ fontSize: 13, fontWeight: 600, color: "var(--ink)" }}>{nom}</div>
									<div style={{ fontSize: 11, color: "var(--ink-soft)" }}>
										{exos.length} exo{exos.length > 1 ? "s" : ""}
									</div>
								</div>
								<span style={{ fontSize: 12, fontWeight: 600, color: tpl._already ? "var(--ink-faint)" : "#30a49a" }}>
									{tpl._already ? "Déjà présent" : (importingId === tplId ? "…" : "Importer →")}
								</span>
							</button>
						);
					})}
				</div>
				<div className="mens-modal-actions">
					<button className="mens-btn ghost" onClick={onClose}>Fermer</button>
				</div>
			</div>
		</div>
	);
}

// ─── Page principale ───
export default function CoachTemplatesLibrary({
	user,
	students,
	// Coach context (loaded by parent)
	coachTemplates,
	coachFolders,
	coachAssoc,
	loadingCoach,
	onRefreshCoachTemplates,
	onOpenCoachTemplate,
	onNewCoachTemplate,
	onCreateCoachFolder,
	onDeleteCoachFolder,
	onAssignCoachFolder,
	// Student context handlers routed back to parent (uses selected student)
	onOpenStudentTemplate,
	onImportCoachTemplateToStudent,
	onBulkAssignTemplate,
	onNotif,
}) {
	// Sélecteur de contexte : null = biblio du coach, sinon = biblio de l'élève
	const [selectedStudentId, setSelectedStudentId] = useState(null);

	// Données élève (chargées à la demande)
	const [studentTemplates, setStudentTemplates] = useState([]);
	const [studentFolders, setStudentFolders] = useState([]);
	const [studentAssoc, setStudentAssoc] = useState([]);
	const [loadingStudent, setLoadingStudent] = useState(false);

	// UI shared
	const [activeFolderKey, setActiveFolderKey] = useState(null);
	const [showNewFolderInput, setShowNewFolderInput] = useState(false);
	const [newFolderName, setNewFolderName] = useState("");
	const [showImportModal, setShowImportModal] = useState(false);

	const isStudentContext = selectedStudentId != null;
	const contextStudent = isStudentContext ? (students || []).find((s) => s.id === selectedStudentId) : null;

	// Reset filtre dossier / création à chaque switch de contexte
	useEffect(() => {
		setActiveFolderKey(null);
		setShowNewFolderInput(false);
		setNewFolderName("");
	}, [selectedStudentId]);

	// Charge templates + dossiers de l'élève au switch
	useEffect(() => {
		if (!isStudentContext) return;
		let cancelled = false;
		setLoadingStudent(true);
		Promise.all([
			getStudentTemplates(selectedStudentId),
			getStudentTemplateFolders(selectedStudentId),
		]).then(([tplRes, folRes]) => {
			if (cancelled) return;
			setStudentTemplates(tplRes.success ? (tplRes.templates || []) : []);
			setStudentFolders(folRes.success ? (folRes.folders || []) : []);
			setStudentAssoc(folRes.success ? (folRes.associations || []) : []);
			setLoadingStudent(false);
		});
		return () => { cancelled = true; };
	}, [selectedStudentId, isStudentContext]);

	const refreshStudent = async () => {
		if (!isStudentContext) return;
		const [tplRes, folRes] = await Promise.all([
			getStudentTemplates(selectedStudentId),
			getStudentTemplateFolders(selectedStudentId),
		]);
		if (tplRes.success) setStudentTemplates(tplRes.templates || []);
		if (folRes.success) {
			setStudentFolders(folRes.folders || []);
			setStudentAssoc(folRes.associations || []);
		}
	};

	// ─── Sources actives selon contexte ───
	const templates = isStudentContext ? studentTemplates : (coachTemplates || []);
	const folders = isStudentContext ? studentFolders : (coachFolders || []);
	const assoc = isStudentContext ? studentAssoc : (coachAssoc || []);
	const loading = isStudentContext ? loadingStudent : loadingCoach;

	// ─── Handlers folders (route selon contexte) ───
	const handleCreateFolder = async () => {
		const name = (newFolderName || "").trim();
		if (!name) return;
		if (isStudentContext) {
			const key = Date.now();
			const res = await saveStudentTemplateFolder(selectedStudentId, key, name);
			if (res.success) {
				setStudentFolders((prev) => [...prev, res.folder || { Key: key, NomDossier: name, Supprime: false }]);
				onNotif?.("Dossier créé");
			} else {
				onNotif?.("Erreur lors de la création du dossier", "error");
			}
		} else {
			await onCreateCoachFolder?.(name);
		}
		setNewFolderName("");
		setShowNewFolderInput(false);
	};

	const handleDeleteFolder = async (folderKey) => {
		if (!window.confirm("Supprimer ce dossier ? Les modèles ne seront pas supprimés.")) return;
		if (isStudentContext) {
			const res = await deleteStudentTemplateFolder(selectedStudentId, folderKey);
			if (res.success) {
				setStudentFolders((prev) => prev.map((f) => (f.Key ?? f.key) === folderKey ? { ...f, Supprime: true } : f));
				setStudentAssoc((prev) => prev.map((a) => (a.FolderKey ?? a.folderKey) === folderKey ? { ...a, Supprime: true } : a));
				if (activeFolderKey === folderKey) setActiveFolderKey(null);
				onNotif?.("Dossier supprimé");
			} else {
				onNotif?.("Erreur lors de la suppression", "error");
			}
		} else {
			await onDeleteCoachFolder?.(folderKey);
			if (activeFolderKey === folderKey) setActiveFolderKey(null);
		}
	};

	const handleToggleFolderAssign = async (template, folderKey, assign) => {
		const templateKey = template.Key ?? template.key;
		if (isStudentContext) {
			const res = await assignStudentTemplateFolder(selectedStudentId, folderKey, templateKey, assign);
			if (res.success) {
				setStudentAssoc((prev) => {
					if (assign) {
						const exists = prev.some((a) => (a.FolderKey ?? a.folderKey) === folderKey && (a.TemplateKey ?? a.templateKey) === templateKey);
						if (exists) return prev.map((a) => (a.FolderKey ?? a.folderKey) === folderKey && (a.TemplateKey ?? a.templateKey) === templateKey ? { ...a, Supprime: false } : a);
						return [...prev, { FolderKey: folderKey, TemplateKey: templateKey, Supprime: false }];
					}
					return prev.map((a) => (a.FolderKey ?? a.folderKey) === folderKey && (a.TemplateKey ?? a.templateKey) === templateKey ? { ...a, Supprime: true } : a);
				});
			}
		} else {
			await onAssignCoachFolder?.(template, folderKey, assign);
		}
	};

	// ─── Handlers templates (route selon contexte) ───
	const handleOpenTemplate = (tpl) => {
		if (isStudentContext) onOpenStudentTemplate?.(tpl, selectedStudentId);
		else onOpenCoachTemplate?.(tpl.Id ?? tpl.id);
	};

	const handleNewTemplate = async () => {
		if (isStudentContext) {
			// Crée un template vide chez l'élève puis l'ouvre dans l'éditeur.
			const res = await createTraining(selectedStudentId, "Nouveau modèle", Date.now(), true, []);
			if (!res.success) {
				onNotif?.("Erreur lors de la création du modèle", "error");
				return;
			}
			await refreshStudent();
			onOpenStudentTemplate?.({ Id: res.trainingId, id: res.trainingId, Nom: "Nouveau modèle", nom: "Nouveau modèle" }, selectedStudentId);
		} else {
			onNewCoachTemplate?.();
		}
	};

	const handleDeleteTemplate = async (tpl) => {
		const tplId = tpl.Id ?? tpl.id;
		const nom = tpl.Nom ?? tpl.nom ?? "ce modèle";
		if (!tplId) return;
		if (!window.confirm(`Supprimer "${nom}" ?`)) return;
		const ownerId = isStudentContext ? selectedStudentId : user.id;
		const res = await deleteTraining(ownerId, tplId);
		if (!res.success) {
			onNotif?.(res.error || "Erreur lors de la suppression", "error");
			return;
		}
		if (isStudentContext) await refreshStudent();
		else await onRefreshCoachTemplates?.();
		onNotif?.("Modèle supprimé");
	};

	const handleImportCoachTemplate = async (coachTemplate) => {
		if (!isStudentContext) return { ok: false };
		const res = await onImportCoachTemplateToStudent?.(selectedStudentId, coachTemplate);
		if (res?.ok) await refreshStudent();
		return res || { ok: false };
	};

	// ─── Rendu ───
	const validFolderKeys = new Set(
		(folders || []).filter((f) => !(f.Supprime ?? f.supprime)).map((f) => (f.Key ?? f.key))
	);

	const displayed = activeFolderKey === null
		? templates
		: templates.filter((t) => assoc.some((a) => {
			const aSupprime = a.Supprime ?? a.supprime;
			const aFolderKey = a.FolderKey ?? a.folderKey;
			const aTemplateKey = a.TemplateKey ?? a.templateKey;
			const tKey = t.Key ?? t.key;
			return !aSupprime && aFolderKey === activeFolderKey && aTemplateKey === tKey;
		}));

	return (
		<div className="coach-templates-view">
			<div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
				{/* Sélecteur de contexte */}
				<div className="coach-exercises-context-selector" style={{ marginBottom: 12 }}>
					<button
						className={`coach-exercises-context-pill ${!isStudentContext ? "active" : ""}`}
						onClick={() => setSelectedStudentId(null)}
					>
						Ma bibliothèque
					</button>
					{(students || []).map((s) => (
						<button
							key={s.id}
							className={`coach-exercises-context-pill ${selectedStudentId === s.id ? "active" : ""}`}
							onClick={() => setSelectedStudentId(s.id)}
						>
							{s.prenom} {s.nom}
						</button>
					))}
				</div>

				<div style={{ display: "flex", gap: 16, flex: 1, minWidth: 0, minHeight: 0 }}>
					{/* Panneau de dossiers */}
					<div className="coach-templates-folders">
						<div className="coach-templates-folders-header">Dossiers</div>
						<div
							onClick={() => setActiveFolderKey(null)}
							className={`coach-templates-folder ${activeFolderKey === null ? "active" : ""}`}
						>
							<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, opacity: 0.7 }}>
								<rect x="3" y="3" width="18" height="18" rx="2" />
								<line x1="3" y1="9" x2="21" y2="9" />
							</svg>
							<span className="coach-templates-folder-name">Tous</span>
							<span className="coach-templates-folder-count">{templates.length}</span>
						</div>

						{folders.filter((f) => !(f.Supprime ?? f.supprime)).map((folder) => {
							const folderKey = folder.Key ?? folder.key;
							const folderName = folder.NomDossier ?? folder.nomDossier ?? "Dossier";
							const count = assoc.filter((a) => {
								const aSupprime = a.Supprime ?? a.supprime;
								const aFolderKey = a.FolderKey ?? a.folderKey;
								return !aSupprime && aFolderKey === folderKey;
							}).length;
							const isActive = activeFolderKey === folderKey;
							return (
								<div
									key={folderKey}
									onClick={() => setActiveFolderKey(folderKey)}
									className={`coach-templates-folder ${isActive ? "active" : ""}`}
								>
									<svg width="14" height="14" viewBox="0 0 24 24" fill={isActive ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, opacity: isActive ? 1 : 0.6 }}>
										<path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
									</svg>
									<span className="coach-templates-folder-name">{folderName}</span>
									<span className="coach-templates-folder-count">{count}</span>
									<button
										onClick={(e) => { e.stopPropagation(); handleDeleteFolder(folderKey); }}
										className="coach-templates-folder-delete"
										title="Supprimer le dossier"
									>×</button>
								</div>
							);
						})}

						<div className="coach-templates-folder-new">
							{showNewFolderInput ? (
								<div className="coach-templates-folder-new-form">
									<input
										autoFocus
										value={newFolderName}
										onChange={(e) => setNewFolderName(e.target.value)}
										onKeyDown={(e) => {
											if (e.key === "Enter") handleCreateFolder();
											if (e.key === "Escape") { setShowNewFolderInput(false); setNewFolderName(""); }
										}}
										placeholder="Nom du dossier"
										className="coach-templates-folder-new-input"
									/>
									<div className="coach-templates-folder-new-buttons">
										<button onClick={handleCreateFolder} className="coach-templates-folder-new-ok">Créer</button>
										<button onClick={() => { setShowNewFolderInput(false); setNewFolderName(""); }} className="coach-templates-folder-new-cancel">Annuler</button>
									</div>
								</div>
							) : (
								<button onClick={() => setShowNewFolderInput(true)} className="coach-templates-folder-new-btn">
									<span style={{ fontSize: "1rem", lineHeight: 1 }}>+</span> Nouveau dossier
								</button>
							)}
						</div>
					</div>

					{/* Liste des templates */}
					<div className="coach-templates-list">
						<div className="coach-templates-listhead">
							<div className="coach-templates-breadcrumb">
								<span>{isStudentContext ? `Modèles de ${contextStudent?.prenom || "l'élève"}` : "Mes modèles"}</span>
								{activeFolderKey !== null && (() => {
									const f = folders.find((x) => (x.Key ?? x.key) === activeFolderKey);
									return (
										<>
											<span className="coach-templates-breadcrumb-sep">›</span>
											<span className="coach-templates-breadcrumb-active">{f ? (f.NomDossier ?? f.nomDossier ?? "Dossier") : ""}</span>
										</>
									);
								})()}
							</div>
							<div style={{ display: "flex", gap: 8 }}>
								{isStudentContext && (
									<button className="mens-btn ghost" onClick={() => setShowImportModal(true)}>
										<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 4, verticalAlign: -2 }}>
											<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
											<polyline points="7 10 12 15 17 10" />
											<line x1="12" y1="15" x2="12" y2="3" />
										</svg>
										Depuis ma bibliothèque
									</button>
								)}
								{!isStudentContext && onBulkAssignTemplate && (
									<button className="mens-btn ghost" onClick={() => onBulkAssignTemplate(null)}>
										<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 4, verticalAlign: -2 }}>
											<path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
											<circle cx="9" cy="7" r="4" />
											<path d="M23 21v-2a4 4 0 0 0-3-3.87" />
											<path d="M16 3.13a4 4 0 0 1 0 7.75" />
										</svg>
										Affecter en masse
									</button>
								)}
								<button className="coach-templates-new-btn" onClick={handleNewTemplate}>
									<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
										<line x1="12" y1="5" x2="12" y2="19" />
										<line x1="5" y1="12" x2="19" y2="12" />
									</svg>
									Nouveau modèle
								</button>
							</div>
						</div>

						<div className="coach-templates-grid">
							{loading ? (
								<div className="coach-templates-empty">Chargement…</div>
							) : displayed.length === 0 ? (
								<div className="coach-templates-empty">
									{activeFolderKey === null
										? (isStudentContext ? "Aucun modèle pour cet élève" : "Aucun modèle")
										: "Aucun modèle dans ce dossier"}
								</div>
							) : displayed.map((template) => {
								const tplId = template.Id ?? template.id;
								const tplKey = template.Key ?? template.key;
								const tplNom = template.Nom ?? template.nom ?? "Sans nom";
								const tplExercises = template.ListExercice ?? template.listExercice ?? template.exercices ?? [];
								const tplFolderKeys = new Set(
									(assoc || [])
										.filter((a) => !(a.Supprime ?? a.supprime) && (a.TemplateKey ?? a.templateKey) === tplKey)
										.map((a) => (a.FolderKey ?? a.folderKey))
										.filter((fk) => validFolderKeys.has(fk))
								);
								const totalSeries = tplExercises.reduce((sum, ex) => {
									if (typeof ex === "string") return sum;
									const series = ex.ListSerie ?? ex.listSerie ?? ex.series ?? [];
									return sum + (Array.isArray(series) ? series.length : 0);
								}, 0);
								return (
									<div key={tplId} className="coach-templates-card" style={{ position: "relative" }}>
										<TemplateFolderMenu
											folders={folders.filter((f) => !(f.Supprime ?? f.supprime))}
											activeFolderKeys={tplFolderKeys}
											onToggle={(folderKey, assign) => handleToggleFolderAssign({ ...template, Key: tplKey }, folderKey, assign)}
										/>
										<button
											type="button"
											onClick={(e) => { e.stopPropagation(); handleDeleteTemplate(template); }}
											title="Supprimer le modèle"
											style={{
												position: "absolute", top: 6, right: 6,
												width: 26, height: 26, borderRadius: 7,
												background: "transparent",
												border: "1px solid rgba(244,67,54,0.30)",
												color: "rgba(244,67,54,0.80)",
												display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer",
												zIndex: 3,
											}}
										>
											<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
												<polyline points="3 6 5 6 21 6" />
												<path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
											</svg>
										</button>
										<div className="coach-templates-card-main" onClick={() => handleOpenTemplate(template)}>
											<div className="coach-templates-card-header">
												<div className="coach-templates-card-name">{tplNom}</div>
												<div className="coach-templates-card-stats">
													{tplExercises.length} exo{tplExercises.length > 1 ? "s" : ""}
													{totalSeries > 0 && ` · ${totalSeries} sér.`}
												</div>
											</div>
											{tplExercises.length > 0 && (
												<ul className="coach-templates-card-exercises">
													{tplExercises.slice(0, 6).map((ex, i) => {
														if (typeof ex === "string") {
															return <li key={i}><span className="ex-name">{ex}</span></li>;
														}
														const exName = ex.NomExercice ?? ex.nomExercice ?? ex.nom ?? "";
														const exSeries = ex.ListSerie ?? ex.listSerie ?? ex.series ?? [];
														const seriesCount = Array.isArray(exSeries) ? exSeries.length : 0;
														return (
															<li key={i}>
																<span className="ex-name">{exName}</span>
																{seriesCount > 0 && <span className="ex-series">× {seriesCount}</span>}
															</li>
														);
													})}
													{tplExercises.length > 6 && (
														<li className="more">+ {tplExercises.length - 6} autre{tplExercises.length - 6 > 1 ? "s" : ""}</li>
													)}
												</ul>
											)}
										</div>
									</div>
								);
							})}
						</div>
					</div>
				</div>
			</div>

			{showImportModal && isStudentContext && (
				<ImportFromCoachModal
					coachTemplates={coachTemplates || []}
					coachFolders={coachFolders || []}
					coachAssoc={coachAssoc || []}
					studentTemplates={studentTemplates}
					onClose={() => setShowImportModal(false)}
					onImport={handleImportCoachTemplate}
				/>
			)}
		</div>
	);
}
